import React from 'react';
import { getTimeRemaining, formatTime12Hour } from '../utils/dateUtils';
import Switch from '@mui/material/Switch';
import { FormControlLabel } from "@mui/material";

const CourseWorkUpcoming = ({ classes, switchOn, setSwitchOn, currentTime, recentClass, userEmail }) => {

    const getAuthenticatedLink = (link, email) => {
        if (!link) return '#';
        const url = new URL(link);
        if (email) {
            url.searchParams.set('authuser', email);
        }
        return url.toString();
    };

    const getDueDate = (work) => {
        return new Date(Date.UTC(
            work.dueDate.year,
            work.dueDate.month - 1,
            work.dueDate.day,
            work.dueTime?.hours ?? 23, //only become 11:59 if assignment is null
            work.dueTime?.minutes ?? 59,
            work.dueTime?.seconds ?? 0
        ));
    };

    const formatDate = (work) => {
        // Create UTC date and let it convert to local time
        const localDate = getDueDate(work);

        // Extract the LOCAL date values
        return `${localDate.getMonth() + 1}/${localDate.getDate()}/${localDate.getFullYear()}`;
    };

    const isSameDay = (a, b) => 
        a.getDate() === b.getDate() &&
        a.getMonth() === b.getMonth() &&
        a.getFullYear() === b.getFullYear(); //arrow function is implicit return unless multiple statements
    
    const isToday = (work) => {
        const due = getDueDate(work);

        return isSameDay(due, new Date());
    };

    const isTomorrow = (work) => {
        const due = getDueDate(work);
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);

        return isSameDay(due, tomorrow);
    };

    const getDateLabel = (work) => {
        if (isToday(work)) return 'Today';
        if (isTomorrow(work)) return 'Tomorrow';
        return formatDate(work);
    };

    // Collect all unsubmitted work from all courses
    const allUnsubmittedWork = [];

    classes.filter(course => recentClass(course)).forEach(course => {
        const unsubmittedWork = course.courseWork.filter(work => {
            const state = work.submission?.state;
            return work.dueDate && state !== 'TURNED_IN' && state !== 'RETURNED';
        });

        unsubmittedWork.forEach(work => {
            allUnsubmittedWork.push({
                ...work,
                courseName: course.name,
                courseSection: course.section,
                courseRoom: course.room
            });
        });
    });

    // Split into past due and upcoming
    const pastDue = allUnsubmittedWork.filter(work => {
        const dueDate = getDueDate(work);
        return dueDate < currentTime;
    });

    const upcoming = allUnsubmittedWork.filter(work => {
        const dueDate = getDueDate(work);
        
        return dueDate >= currentTime;
    });

    // Sort by due date
    const sortByDate = (a, b) => {
        const dateA = getDueDate(a);
        const dateB = getDueDate(b);
        return dateA - dateB;
    };

    pastDue.sort(sortByDate);
    upcoming.sort(sortByDate);

    const renderAssignment = (work, index, color) => (
        <div key={index} className="canvas-event-item" style={{ borderLeft: '4px solid ' + color}}>
            <div className="event-date-badge" style={{color}}>
                {getDateLabel(work)}
            </div>

            <div className="event-header">
                <a
                    href={getAuthenticatedLink(work.alternateLink, userEmail)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="event-title-link" 
                    
                >
                    {work.title}
                </a>
            </div>

            <div className="event-time">
                Due: {formatDate(work)}{formatTime12Hour(work.dueTime)}
                <span style={{fontStyle: 'italic', marginLeft: '10px'}}>
                ({getTimeRemaining({
                    ...work.dueDate,
                    hours: work.dueTime?.hours,
                    minutes: work.dueTime?.minutes
                }, currentTime)})
                </span>
            </div>

            <div className="event-course-info">
                {/* gets google classrooms stored class period number, issue if the period is in the title. 
                Also does "1st", "2nd", so "Period" needs to be after */}
                {work.courseSection && ( // green: 4CAF50, red: e13f2b
                    <span style={{fontweight: 500, color}}> {work.courseSection} Period</span> 
                )}
                {/* If no period # is marked in google classroom, it will just show the classes name. 
                Using course selection, we should know the period # anyways. (fix later)*/}
                <span style={{color: '#9e9e9e'}}>{work.courseName}</span>
            </div>
        </div>
    );

    if (pastDue.length === 0 && upcoming.length === 0) { //issue here, while loading classes site displays "No upcoming assignments"
        return (
            <div className="canvas-events-box">
                <p style={{ color: '#9e9e9e', textAlign: 'center' }}>No upcoming assignments</p>
            </div>
        );
    }

    const assignmentsList = (list, message, color) =>{
        return(
            <div className="events-section">
                
                <h3 className="section-title" style={{color}}>{message}</h3>
                <div className="events-list">
                {pastDue.length > 0 ? (
                    list.map((work, index) => renderAssignment(work, index, color))
                ) : (
                    <p style={{ color: '#9e9e9e', margin: '8px 0' }}>None</p>
                )}
                </div>
            </div>      
        );
    };

    return (

        <div className="canvas-events-box">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2 style={{ margin: 0 }}>Google Classroom Assignments</h2>
                
                <FormControlLabel
                    // label={switchOn ? "Hide late assignments" : "Show late assignments"}
                    label = "Show late assignments"
                    control={
                        <Switch
                            color="secondary"
                            checked={switchOn}
                            onChange={(e) => setSwitchOn(e.target.checked)}
                            size="small"
                        />
                    }
                    style={{ margin: 0 }}
                />
            </div>

            {/* Upcoming Assignments */}
            {assignmentsList(upcoming, "Upcoming", '#4CAF50')}

            {/* Past Due - only show if switch is on */}
            {switchOn && assignmentsList(pastDue, "Past Due", '#dc2626')}

          
        </div>
    );
};

export default CourseWorkUpcoming;